import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import cn from 'classnames';
import './PlaygroundChat.scss';

const PlaygroundChat = ({
  selectedThread,
  messages,
  briefComplete,
  isLoading,
  datakeeperLoading,
  onSend,
  onReset,
  onRegenerate,
  onRunDatakeeper,
  onGrade
}) => {
  const [input, setInput] = useState('');
  const messagesRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, []);

  useEffect(() => {
    resizeTextarea();
  }, [input, resizeTextarea]);

  const handleSubmit = e => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    onSend(input.trim());
    setInput('');
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!selectedThread) {
    return (
      <div className='playground-chat'>
        <div className='chat-empty'>
          <div className='empty-icon'>💬</div>
          <p>Select a test shopper to begin</p>
        </div>
      </div>
    );
  }

  return (
    <div className='playground-chat'>
      <div className='chat-header'>
        <div className='chat-header-info'>
          <span className='chat-title'>Dispatcher Chat</span>
          <span className='chat-shopper'>Testing as: {selectedThread.testShopperName}</span>
        </div>
        <div className='chat-header-actions'>
          {briefComplete && <span className='brief-badge'>Brief Complete</span>}
          <button className='reset-btn' onClick={onReset} disabled={messages.length === 0}>
            Reset
          </button>
        </div>
      </div>

      <div className='chat-messages' ref={messagesRef}>
        {messages.length === 0 ? (
          <div className='chat-start'>
            <p>Start a conversation as {selectedThread.testShopperName}</p>
            <span className='hint'>Type a message below to test the dispatcher</span>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={msg.id || i} className={cn('message', msg.role)}>
              <div className='message-content'>{msg.role === 'assistant' ? <ReactMarkdown>{msg.content}</ReactMarkdown> : msg.content}</div>
              <div className='message-footer'>
                {msg.timestamp && <span className='message-time'>{new Date(msg.timestamp).toLocaleTimeString()}</span>}
                {msg.role === 'assistant' && (
                  <button className='regenerate-btn' onClick={() => onRegenerate(i)} disabled={isLoading} title='Regenerate response'>
                    Regenerate
                  </button>
                )}
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className='message assistant loading'>
            <div className='typing-indicator'>
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
      </div>

      <form className='chat-input-form' onSubmit={handleSubmit}>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='Type a message...'
          disabled={isLoading}
          rows={1}
          autoFocus
        />
        <div className='chat-action-buttons'>
          <button
            className={cn('datakeeper-btn', { loading: datakeeperLoading })}
            type='button'
            onClick={onRunDatakeeper}
            disabled={messages.length < 2 || isLoading || datakeeperLoading}
          >
            {datakeeperLoading ? 'Running Datakeeper...' : 'Run Datakeeper'}
          </button>
          <button className='grade-btn' type='button' onClick={onGrade} disabled={messages.length === 0}>
            Grade + Save
          </button>
          <button className='send-btn' type='submit' disabled={!input.trim() || isLoading}>
            Send
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlaygroundChat;
